import { useEffect } from "react";

/**
 * Samples the average warm-shifted color from an image URL and writes it to
 * a CSS variable on document.documentElement. Used by Hero to tint the
 * scroll-progress bar to the current painting's dominant hue.
 *
 * Fails silently on CORS-blocked images — site keeps its crimson default.
 */
export default function useAccentSampler(imageUrl, cssVar = "--accent-dynamic") {
  useEffect(() => {
    if (!imageUrl) return;
    if (typeof document === "undefined") return;

    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";

    img.onload = () => {
      if (cancelled) return;
      try {
        const w = 24;
        const h = 24;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;

        let r = 0,
          g = 0,
          b = 0,
          n = 0;
        for (let i = 0; i < data.length; i += 4) {
          const rr = data[i];
          const gg = data[i + 1];
          const bb = data[i + 2];
          // Skip near-bone and near-ink pixels so the accent reads as a colour.
          const lum = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
          if (lum < 30 || lum > 235) continue;
          const sat = Math.max(rr, gg, bb) - Math.min(rr, gg, bb);
          if (sat < 20) continue;
          r += rr;
          g += gg;
          b += bb;
          n++;
        }
        if (n === 0) return;
        r = Math.round(r / n);
        g = Math.round(g / n);
        b = Math.round(b / n);
        // Slight saturation boost
        const mx = Math.max(r, g, b);
        const k = mx > 0 ? Math.min(1.15, 200 / mx) : 1;
        r = Math.min(255, Math.round(r * k));
        g = Math.min(255, Math.round(g * k));
        b = Math.min(255, Math.round(b * k));
        document.documentElement.style.setProperty(cssVar, `rgb(${r}, ${g}, ${b})`);
      } catch (_err) {
        // CORS or paint failure — silently keep default accent.
        void _err;
      }
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl, cssVar]);
}
